import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Paper,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import AppDataGrid from '../../common/AppDataGrid';

const MOVEMENT_TYPES = [
  { value: 'ALL', labelKey: 'stockMovements.types.ALL' },
  { value: 'PURCHASE', labelKey: 'stockMovements.types.PURCHASE' },
  { value: 'SALE', labelKey: 'stockMovements.types.SALE' },
  { value: 'ADJUSTMENT', labelKey: 'stockMovements.types.ADJUSTMENT' },
  { value: 'DAMAGE', labelKey: 'stockMovements.types.DAMAGE' },
  { value: 'RETURN', labelKey: 'stockMovements.types.RETURN' },
  { value: 'TRANSFER', labelKey: 'stockMovements.types.TRANSFER' },
];

// The backend isn't consistent about movementType casing — "Purchase"/"Sale" are
// written mixed-case (OrderService/PurchaseOrderService), "ADJUSTMENT" is uppercase
// (StockMovementService), and DAMAGE/RETURN/TRANSFER come through a free-text field
// with no enum validation. Normalize before comparing/translating so the filter and
// the Chip label work regardless of what casing a given movement was written with.
const normalizeMovementType = (type) => (type || '').toUpperCase();

const getMovementTypeColor = (type) => {
  switch (normalizeMovementType(type)) {
    case 'PURCHASE': return 'success';
    case 'SALE': return 'info';
    case 'ADJUSTMENT': return 'warning';
    case 'DAMAGE': return 'error';
    case 'RETURN': return 'primary';
    case 'TRANSFER': return 'secondary';
    default: return 'default';
  }
};

const ProductStockMovements = ({ productId }) => {
  const { t } = useTranslation();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('ALL');

  useEffect(() => {
    if (!productId) return;
    const fetchMovements = async () => {
      setLoading(true);
      try {
        const data = await productService.getProductStockMovements(productId);
        setMovements(data);
      } catch (error) {
        toast.error(t('stockMovements.fetchError'));
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchMovements();
  }, [productId, t]);

  const filtered =
    typeFilter === 'ALL'
      ? movements
      : movements.filter((m) => normalizeMovementType(m.movementType) === typeFilter);

  const columns = [
    {
      field: 'createdAt',
      headerName: t('common.date'),
      width: 180,
      cellRenderer: (params) =>
        params.value ? new Date(params.value).toLocaleString() : '-',
    },
    {
      field: 'variantName',
      headerName: t('stockMovements.variant'),
      width: 200,
    },
    {
      field: 'variantSku',
      headerName: t('products.sku'),
      width: 130,
    },
    {
      field: 'movementType',
      headerName: t('stockMovements.movementType'),
      width: 140,
      cellRenderer: (params) => (
        <Chip
          label={t(`stockMovements.types.${normalizeMovementType(params.value)}`, params.value)}
          color={getMovementTypeColor(params.value)}
          size="small"
        />
      ),
      // Show the same translated label the Chip displays — the raw backend value
      // (params.value) is inconsistently-cased English, not what the user sees.
      tooltipValueGetter: (params) =>
        t(`stockMovements.types.${normalizeMovementType(params.value)}`, params.value),
    },
    {
      field: 'quantityChange',
      headerName: t('stockMovements.quantityChange'),
      width: 120,
      cellRenderer: (params) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={params.value > 0 ? 'success.main' : 'error.main'}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          {params.value > 0 ? '+' : ''}
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'referenceType',
      headerName: t('stockMovements.referenceType'),
      width: 160,
      cellRenderer: (params) => params.value || '-',
    },
    {
      field: 'createdByUserName',
      headerName: t('stockMovements.createdBy'),
      width: 150,
      cellRenderer: (params) => params.value || '-',
    },
    {
      field: 'notes',
      headerName: t('common.notes'),
      flex: 1,
      minWidth: 200,
      cellRenderer: (params) => params.value || '-',
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          select
          label={t('stockMovements.movementType')}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          {MOVEMENT_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {t(type.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Paper variant="outlined">
        <AppDataGrid
          rows={filtered}
          columns={columns}
          loading={loading}
          pageSize={25}
          pageSizeOptions={[10, 25, 50]}
          getRowId={(row) => row.id}
        />
      </Paper>
    </Box>
  );
};

export default ProductStockMovements;
